/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { STARCTypography } from '@/components/STARCTypography';
import {
  CMSSpacer,
  type CMSSpacerProps,
} from '@/features/contentstack/components/CMSSpacer/CMSSpacer';
import { type ContentStackCertona, type ContentStackPost } from '@/features/contentstack/interface';
import { CMSProductCard } from './CMSProductCard/CMSProductCard';
import { CMSModularHandler as CMSLPModularHandler } from '../../landingPage/components/CMSModularHandler';
import { View } from '@az/starc-ui';
import styles from './Blog.module.scss';
import { ImageVideoContent } from './BlogImageVideoContent/ImageVideoContent';
import { CMSBlogRichText } from './CMSBlogRichText/CMSBlogRichText';
import { CMSBlogFaq } from './CMSBlogFaq/CMSBlogFaq';
import { BlogProductTypeGrid } from './BlogProductGrid/BlogProductTypeGrid';
import { CMSImage } from '@/features/contentstack/components/CMSImage/CMSImage';
import { CMSIframe } from '@/features/contentstack/components/CMSIframe/CMSIframe';
import { CMSCertona } from '@/features/contentstack/components/CMSCertona/CMSCertona';
import { type CertonaRecommendation } from '@/features/certona';
import { getCMSModularBlockId } from '@/features/contentstack/utils/getCMSModularBlockId';
import { CMSPencilBanner } from '@/features/contentstack/components/CMSPencilBanner/CMSPencilBanner';

const FALLBACK_POSTER_IMAGE = '/images/video-default-poster-gray.svg';

const CMSBlogSpacer = ({ children, ...rest }: { id?: string } & CMSSpacerProps) => (
  <CMSSpacer removeHorizontalSpacing landingPage {...rest}>
    {children}
  </CMSSpacer>
);

type CMSModularHandlerProps = {
  content: ContentStackPost['post_content'];
  certonaRecommendations?: CertonaRecommendation[];
  blogCreatedDate: string | undefined;
};

export const CMSModularHandler = ({
  content,
  certonaRecommendations,
  blogCreatedDate,
}: CMSModularHandlerProps) => {
  return (
    <STARCTypography>
      {content?.map((modularBlock, i) => {
        const getBlockId = (blockName: string) => getCMSModularBlockId(modularBlock, blockName);
        if (modularBlock.product_grid) {
          return (
            <CMSBlogSpacer
              key={`product-grid-${modularBlock.product_grid.headline}`}
              id={getBlockId('product_grid')}
            >
              <BlogProductTypeGrid content={modularBlock.product_grid} gridIndex={i} />
            </CMSBlogSpacer>
          );
        }

        if (modularBlock.product_card) {
          return (
            <CMSBlogSpacer
              key={`product-card-${modularBlock.product_card.title}`}
              id={getBlockId('product_card')}
            >
              <CMSProductCard content={modularBlock.product_card} />
            </CMSBlogSpacer>
          );
        }
        if (modularBlock.landingpage_component?.component) {
          return (
            <CMSBlogSpacer key={`lp-component-${i}`} id={getBlockId('landingpage_component')}>
              <View className={styles.spacingControl}>
                <CMSLPModularHandler content={modularBlock.landingpage_component.component} />
              </View>
            </CMSBlogSpacer>
          );
        }
        if (modularBlock.copy_media) {
          return (
            <CMSBlogSpacer
              key={`copy-media-${modularBlock.copy_media.heading}`}
              id={getBlockId('copy_media')}
            >
              <ImageVideoContent
                content={modularBlock.copy_media}
                preload={'auto'}
                mediaType={modularBlock?.copy_media?.media_type}
                blogCreatedDate={blogCreatedDate}
              />
            </CMSBlogSpacer>
          );
        }
        if (modularBlock.post_rte) {
          return (
            <div id={getBlockId('post_rte')} key={`post-rte-${i}`}>
              {modularBlock.post_rte.content?.map((textContent, index) => (
                <CMSBlogSpacer key={`rte-${i}-${index}`}>
                  <CMSBlogRichText content={textContent} />
                </CMSBlogSpacer>
              ))}
            </div>
          );
        }

        if ((modularBlock.faq?.faq?.length ?? 0) > 0) {
          return (
            <CMSBlogSpacer key={`faq-${modularBlock.faq?.title}`} id={getBlockId('faq')}>
              <CMSBlogFaq content={modularBlock.faq} />
            </CMSBlogSpacer>
          );
        }

        if (modularBlock.image?.image_url) {
          return (
            <CMSBlogSpacer key={`image-${modularBlock.image?.image_url}`} id={getBlockId('image')}>
              <CMSImage image={modularBlock.image} />
            </CMSBlogSpacer>
          );
        }

        if (modularBlock.video?.video_url) {
          return (
            <CMSBlogSpacer key={`video-${modularBlock.video?.video_url}`} id={getBlockId('video')}>
              <video
                preload="auto"
                controlsList="nodownload"
                controls
                src={modularBlock.video.video_url}
                width="100%"
                poster={modularBlock.video.poster_image ?? FALLBACK_POSTER_IMAGE}
              />
            </CMSBlogSpacer>
          );
        }

        if (modularBlock.certona) {
          const certonaContent = {
            ...(modularBlock.certona as ContentStackCertona),
            title: `Blog ${modularBlock.certona.container_id}`,
          };
          return (
            <CMSBlogSpacer
              key={`certona-${modularBlock.certona.container_id}`}
              id={getBlockId('certona')}
            >
              <CMSCertona
                content={certonaContent}
                certonaRecommendations={certonaRecommendations}
              />
            </CMSBlogSpacer>
          );
        }

        if ('iframe' in modularBlock && modularBlock.iframe?.iframe_url) {
          const content = {
            source_url: { href: modularBlock.iframe.iframe_url, title: '' },
            title: '',
            iframe_height: {
              desktop: 400,
              tablet: 400,
              mobile: 192,
            },
          };
          return (
            <CMSBlogSpacer
              key={`iframe-${modularBlock.iframe?.iframe_url}`}
              id={getBlockId('iframe')}
            >
              <CMSIframe content={content} />
            </CMSBlogSpacer>
          );
        }

        if ('pencil_banner' in modularBlock && modularBlock.pencil_banner?.pencil_banner?.[0]) {
          return (
            <CMSBlogSpacer
              key={`pencil-banner-${modularBlock.pencil_banner.pencil_banner[0].uid}`}
              {...(modularBlock.pencil_banner.pencil_banner[0].component_id && {
                id: modularBlock.pencil_banner.pencil_banner[0].component_id,
              })}
            >
              <CMSPencilBanner content={modularBlock.pencil_banner.pencil_banner[0]} />
            </CMSBlogSpacer>
          );
        }

        return null;
      })}
    </STARCTypography>
  );
};
